import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import axios from "axios";
import TextInput from "src/Components/Authentication/TextInput";
import PhoneNumberInput from "src/Components/Authentication/PhoneNumberInput";
import PasswordInput from "src/Components/Authentication/PasswordInput";
import ConfirmPasswordInput from "src/Components/Authentication/ConfirmPasswordInput";
import BlueButton from "src/Components/Authentication/BlueButton";
import { validatePassword, API_URL, PASSWORD_HINT } from "src/Common/function";
import InviteService from "src/Services/API/invite.service";
import AuthService from "src/Services/API/auth.service";
import TokenService from "src/Services/API/token.service";
import UserService from "src/Services/API/user.service";
import PermissionService from "src/Services/API/permission.service";
import { setAuthState } from "src/Redux/Slices/AuthSlice";

function SetPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  let token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [invite, setInvite] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({ name: false, phone: false, password: false, confirmPassword: false });

  // Pre-fill the name the inviter entered when sending the invitation. The
  // invitee can still edit it. A failure here is not surfaced: they can simply
  // type their name, and an invalid token is reported on submit.
  useEffect(() => {
    if (!token) return;
    InviteService.getByToken(token)
      .then((response) => {
        setInvite(response.data);
        if (response.data?.name) setName(response.data.name);
      })
      .catch(() => {});
  }, [token]);

  // An Administrator is a new tenant's owner: sign them straight in and hand
  // them to onboarding, which needs their account to read the company. Everyone
  // else lands on the login page as before.
  const afterAccept = () => {
    if (invite?.role !== "Administrator" || !invite?.email) {
      toast("Password has been set");
      navigate("/");
      return;
    }
    AuthService.login(invite.email, password)
      .then(async (response) => {
        TokenService.updateLocalAccessToken(response.data.accessToken);
        UserService.saveCurrentUser(response.data.user);
        try {
          await PermissionService.loadAndStore();
        } catch (e) {
          console.log("failed to load permissions: ", e);
        }
        dispatch(setAuthState(true));
        navigate("/onboarding");
      })
      .catch(() => {
        // The account exists either way — fall back to the login page.
        toast("Password has been set. Please log in.");
        navigate("/");
      });
  };

  const blueButtonClicked = () => {
    setSubmitted(true);
    const passwordResult = validatePassword(password);
    const nextErrors = {
      name: !name.trim(),
      phone: !phone,
      password: !passwordResult.success,
      confirmPassword: !confirmPassword || password !== confirmPassword,
    };
    setErrors(nextErrors);

    if (nextErrors.name) {
      toast("Please enter your name");
      return;
    }
    if (nextErrors.phone) {
      toast("Please enter a valid phone number");
      return;
    }
    if (nextErrors.password) {
      toast(passwordResult.message || "Please enter strong password");
      return;
    }
    if (nextErrors.confirmPassword) {
      toast("Passwords doesn't match");
      return;
    }

    setIsLoading(true);
    InviteService.accept({ token, password, name, phone })
      .then(function () {
        afterAccept();
        setIsLoading(false);
      })
      .catch(function (error) {
        console.log(error);
        // Surface the server's message (e.g. an already-accepted invite)
        // instead of a generic error.
        if (error.response?.data?.message) {
          toast(error.response.data.message);
        } else {
          toast("An error occurred");
        }
        setIsLoading(false);
      });
  };

  return (
    <div className="w-screen h-screen flex items-center justify-center">
      <div className="w-[332px] h-[411px] flex items-center justify-center">
        <div className="w-[296px] h-[400px]">
          <p className="font-OpenSansRegular text-[16px] text-titleColor text-center font-[700]">Set Password</p>
          <p className="font-OpenSansSemiBold text-[12px] text-subTitleColor text-center mt-[5px]">
            Fill in your name and password. {PASSWORD_HINT}
          </p>
          <div className="mt-[20px]" />
          <TextInput
            value={name}
            onTextChanged={(v) => {
              setName(v);
              if (errors.name) setErrors((e) => ({ ...e, name: false }));
            }}
            error={errors.name}
          />
          <PhoneNumberInput
            label="Phone number"
            value={phone}
            required
            showError={submitted}
            onChange={(v) => {
              setPhone(v);
              if (errors.phone) setErrors((e) => ({ ...e, phone: false }));
            }}
            labelClassName="text-[12px] font-OpenSansMedium font-[600] text-inputEmailTitle"
          />
          <div className="mt-[10px]" />
          <PasswordInput
            onPasswordChanged={(v) => {
              setPassword(v);
              if (errors.password) setErrors((e) => ({ ...e, password: false }));
            }}
            isForgotButton={false}
            error={errors.password}
          />
          <ConfirmPasswordInput
            onConfirmpasswordChanged={(v) => {
              setConfirmPassword(v);
              if (errors.confirmPassword) setErrors((e) => ({ ...e, confirmPassword: false }));
            }}
            error={errors.confirmPassword}
          />
          <BlueButton onButtonClicked={blueButtonClicked} title={"SET PASSWORD"} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
}

export default SetPassword;
